/**
 * store/video.ts — the «видео» slice: the list of clips, the one in the player, and how it is
 * going.
 *
 * Task 13. `spec.md` «Плеер»: «Хранилище состояния разбито на срезы: сессия, плеер, очередь,
 * библиотека, видео». Its own slice for the reason `store/library.ts` gives: a page of clips
 * landing here re-renders nothing of the music, and the four state changes a started clip
 * makes re-render nothing of the list.
 *
 * Its parts, each written from one place:
 *
 * - **the list** — {@link VideoState.load} and {@link VideoState.loadMore}, from the server
 *   (`GET /api/videos`), newest post first, both kinds together;
 * - **the player** — the clip shown ({@link VideoState.selected}) and what its frame is doing
 *   (`frame`, `playback`, `problem`). Written by `engine/video.ts` from the player's own
 *   events, and by nothing else; the screen reads it;
 * - **the field** (task 76) — {@link VideoState.setSearchText}: what is in the field over the
 *   list, and what the server found for it;
 * - **«Избранные»** (task 77) — {@link VideoState.view} and {@link VideoState.favoriteClips}:
 *   which list the switch over it shows, and the clips with the listener's heart.
 *
 * Task 75: the list itself is what plays in a row (`spec.md` «Видео»). The clip after the one
 * shown is the next, down the list, from newer to older ({@link neighbours}); there is no queue
 * of clips beside the list, and nothing here ever reaches `store/queue.ts`.
 *
 * Task 76: the field narrows the list to what the server finds, and what plays in a row is then
 * the clips found ({@link playlist}). The whole list stays under the answer as it was, so an
 * emptied field gives it back without a request. When the requests go is «Поиск»'s rule
 * (`lib/search-as-you-type.ts`) — with one rule more: a single letter sends nothing.
 *
 * Task 77: «Избранные» is the listener's own list, and what plays in a row there. The hearts
 * themselves are `store/favorites.ts`'s, one set for tracks and clips; this slice keeps the clips
 * with a heart, newest heart first. A heart taken off in «Избранные» takes the clip out of what
 * plays at once and leaves its row where it is while the list is looked at — as in «Моё», a row
 * gone from under the thumb would hand the next tap to another clip, and a second tap gives the
 * heart back. The next look — the switch, or an entry into the tab — leaves it out. The clip in
 * the frame keeps its place in the row of clips even when its own heart is taken off: the one
 * after it comes next. So what plays reads both slices: {@link playlistNow} for the engine,
 * {@link usePlaylist} for the screen.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { MIN_PREFIX_LENGTH, normalizeText } from '@fdm/shared';

import { fetchFavoriteVideos, fetchVideoSearch, fetchVideos } from '../lib/api';
import type { ApiFailure, StreamLink, VideoItem, VideoPage, YoutubeRef } from '../lib/api';
import { searchAsYouType } from '../lib/search-as-you-type';

import { useFavorites } from './favorites';
import type { SearchStatus } from './search';
import { useSession } from './session';

/** The list's own progress. */
export type VideoListStatus = 'idle' | 'loading' | 'ready' | 'failed';

/**
 * The frame, from the tab's point of view:
 *
 * - `idle` — no player: the tab is not on screen, or there is nothing to play;
 * - `loading` — YouTube's script and frame are on their way;
 * - `ready` — the player answers;
 * - `unavailable` — eight seconds went by without it (`engine/youtube.ts`): «без VPN».
 */
export type VideoFrame = 'idle' | 'loading' | 'ready' | 'unavailable';

/**
 * What the clip in the frame is doing, by the player's own word — and one word of ours:
 * `starting`, a start asked for (a tap, «Дальше», the end of the last clip) that the player has
 * not answered yet. Task 75: it keeps the gold «играть» off the frame between two clips, and it
 * lets the music, started meanwhile, stop the clip on its way (`engine/video.ts`).
 */
export type VideoPlayback = 'idle' | 'starting' | 'buffering' | 'playing' | 'paused' | 'ended';

/**
 * - `refused` — YouTube's errors 101 and 150, «встраивание запрещено»: shown as «без VPN»,
 *   as `spec.md` «Видео» asks, with the button that opens the clip on YouTube itself;
 * - `failed` — any other error of the player: a removed video, a broken one.
 */
export type VideoProblem = 'refused' | 'failed';

/** Task 77: which list the switch over the list shows. */
export type VideoView = 'all' | 'favorites';

/** Task 76: what the field over the list found. */
export interface VideoSearch {
  /** The normalised query this answer is for. */
  readonly query: string;
  /** The field's text when it was asked — what «ничего не нашлось» quotes, not the field now. */
  readonly text: string;
  /** Every clip found, newest post first. */
  readonly items: readonly VideoItem[];
}

export interface VideoState {
  readonly items: readonly VideoItem[];
  readonly nextCursor: string | null;
  readonly total: number | undefined;
  readonly status: VideoListStatus;
  /** The next page, while it loads — the list itself stays on screen. */
  readonly loadingMore: boolean;
  readonly failure: ApiFailure | undefined;

  /** The clip in the player area. Kept when the tab is left, so coming back shows it again. */
  readonly selected: VideoItem | undefined;
  readonly frame: VideoFrame;
  readonly playback: VideoPlayback;
  readonly problem: VideoProblem | undefined;

  /** Task 76: exactly what is in the field over the list. */
  readonly searchText: string;
  /**
   * The last answer for a field with something in it, kept while the next one loads.
   * `undefined` is the whole list: the field empty, or nothing answered yet.
   */
  readonly search: VideoSearch | undefined;
  readonly searchStatus: SearchStatus;
  readonly searchFailure: ApiFailure | undefined;

  /** Task 77: «Все» or «Избранные». Kept when the tab is left, like the field. */
  readonly view: VideoView;
  /**
   * Task 77: the rows of «Избранные» — the server's answer, newest heart first, headed by the
   * clips hearted here since. `undefined` until the first answer. A clip whose heart is taken off
   * keeps its row until the next look, and does not play (see the header).
   */
  readonly favoriteClips: readonly VideoItem[] | undefined;
  readonly favoriteClipsFailure: ApiFailure | undefined;

  /** The first page, afresh: every entry into the tab. */
  readonly load: () => Promise<void>;
  /**
   * The page after the last one shown; `true` once it has landed in the list. Asked again while
   * it is on its way, it answers with the same request — so «Дальше» tapped then waits for the
   * page the player already asked for.
   */
  readonly loadMore: () => Promise<boolean>;
  /** Task 76: the field changed — a request after a pause in typing, or the whole list back. */
  readonly setSearchText: (text: string) => void;
  /** Task 76: asks again for what is in the field, now. */
  readonly retrySearch: () => void;
  /** Task 77: the clips with a heart, afresh: every entry into the tab — a new look. */
  readonly loadFavoriteClips: () => Promise<void>;
  /** Task 77: a section chosen; «Избранные» chosen is a new look. */
  readonly setView: (view: VideoView) => void;
  /**
   * Task 77: a heart put on `clip` here — it heads «Избранные» at once, before any answer. A row
   * of «Избранные» given its heart back while the list is looked at stays where it is.
   */
  readonly noteFavoriteClip: (clip: VideoItem) => void;
}

const EMPTY = Object.freeze({
  items: [],
  nextCursor: null,
  total: undefined,
  status: 'idle',
  loadingMore: false,
  failure: undefined,
  selected: undefined,
  frame: 'idle',
  playback: 'idle',
  problem: undefined,
  searchText: '',
  search: undefined,
  searchStatus: 'idle',
  searchFailure: undefined,
  view: 'all',
  favoriteClips: undefined,
  favoriteClipsFailure: undefined,
} as const);

/** A request for the first page carries a number; an answer to an older one is dropped. */
let latest = 0;

/** The next page on its way, while it is. See {@link VideoState.loadMore}. */
let pageInFlight: Promise<boolean> | undefined;

/** Task 77: the number of the newest request for the clips with a heart. */
let favoritesLatest = 0;
/** Clips hearted here since that request went: its answer may have been read before them. */
let notedSinceAsked: readonly VideoItem[] = [];

/** Task 77: a new look at «Избранные» leaves out the rows whose heart was taken off. */
function lookAgainAtFavorites(): void {
  const { favoriteClips } = useVideo.getState();
  if (favoriteClips === undefined) return;
  const hearts = useFavorites.getState().ids;
  const kept = favoriteClips.filter((clip) => hearts.has(clip.id));
  if (kept.length !== favoriteClips.length) useVideo.setState({ favoriteClips: kept });
}

const typing = searchAsYouType<VideoPage>({
  fetch: async (text) => await fetchVideoSearch(useSession.getState().session?.token, text),
  answered: () => {
    const { searchStatus, search } = useVideo.getState();
    return searchStatus === 'ready' ? search?.query : undefined;
  },
  onLoading: () => {
    useVideo.setState({ searchStatus: 'loading', searchFailure: undefined });
  },
  onAnswer: (page, text, query) => {
    useVideo.setState({
      search: { query, text, items: page.items },
      searchStatus: 'ready',
      searchFailure: undefined,
    });
  },
  onFailure: (failure) => {
    useVideo.setState({ searchStatus: 'failed', searchFailure: failure });
  },
});

export const useVideo = create<VideoState>((set, get) => ({
  ...EMPTY,

  load: async () => {
    latest += 1;
    const mine = latest;
    // The rows of the last visit stay while the new ones come: the list does not blink
    // empty on every entry, and it does not move under the thumb either — it is replaced
    // once, when the answer lands.
    set({ status: get().items.length > 0 ? 'ready' : 'loading', failure: undefined });
    const result = await fetchVideos(useSession.getState().session?.token);
    if (mine !== latest) return;
    if (result.ok) {
      set({
        items: result.data.items,
        nextCursor: result.data.nextCursor,
        total: result.data.total,
        status: 'ready',
        failure: undefined,
      });
    } else {
      set({ status: 'failed', failure: result.error });
    }
  },

  loadMore: () => {
    if (pageInFlight !== undefined) return pageInFlight;
    const { nextCursor } = get();
    if (nextCursor === null) return Promise.resolve(false);
    const mine = latest;
    set({ loadingMore: true });
    pageInFlight = fetchVideos(useSession.getState().session?.token, nextCursor).then((result) => {
      pageInFlight = undefined;
      if (mine !== latest) {
        set({ loadingMore: false });
        return false;
      }
      if (!result.ok) {
        set({ loadingMore: false, failure: result.error });
        return false;
      }
      set((state) => ({
        items: [...state.items, ...result.data.items],
        nextCursor: result.data.nextCursor,
        loadingMore: false,
      }));
      return true;
    });
    return pageInFlight;
  },

  setSearchText: (text) => {
    set({ searchText: text });
    // One letter would be searched as a whole word, and the list would blank between the first
    // keystroke and the second: the field waits for the letter from which a word is found by its
    // start. Until then — and for an empty field — the whole list, at once.
    if (normalizeText(text).length >= MIN_PREFIX_LENGTH) {
      typing.type(text);
      return;
    }
    typing.abandon();
    set({ search: undefined, searchStatus: 'idle', searchFailure: undefined });
  },

  retrySearch: () => {
    typing.retry(get().searchText);
  },

  loadFavoriteClips: async () => {
    favoritesLatest += 1;
    const mine = favoritesLatest;
    notedSinceAsked = [];
    lookAgainAtFavorites();
    const result = await fetchFavoriteVideos(useSession.getState().session?.token);
    if (mine !== favoritesLatest) return;
    if (!result.ok) {
      set({ favoriteClipsFailure: result.error });
      return;
    }
    // The answer's own clips are not checked against this phone's hearts: a heart put on another
    // phone is in the answer before it is in the set (`store/favorites.ts` reads it again).
    const answered = new Set(result.data.items.map((clip) => clip.id));
    const hearts = useFavorites.getState().ids;
    const newer = notedSinceAsked.filter((clip) => !answered.has(clip.id) && hearts.has(clip.id));
    set({ favoriteClips: [...newer, ...result.data.items], favoriteClipsFailure: undefined });
  },

  setView: (view) => {
    set({ view });
    if (view === 'favorites') lookAgainAtFavorites();
  },

  noteFavoriteClip: (clip) => {
    notedSinceAsked = [clip, ...notedSinceAsked.filter((other) => other.id !== clip.id)];
    const { favoriteClips, view } = get();
    if (favoriteClips === undefined) return;
    if (view === 'favorites' && favoriteClips.some((other) => other.id === clip.id)) return;
    set({ favoriteClips: [clip, ...favoriteClips.filter((other) => other.id !== clip.id)] });
  },
}));

/** A YouTube link the server vouched for: YouTube's player plays it. */
export type YoutubeClip = VideoItem & { readonly kind: 'youtube'; readonly youtube: YoutubeRef };

/** An mp4 of the channel with its address: the one video element plays it (task 14). */
export type Mp4Clip = VideoItem & { readonly kind: 'video'; readonly stream: StreamLink };

export type PlayableClip = YoutubeClip | Mp4Clip;

/** A clip the player can play: a YouTube link or an mp4 the server handed an address for. */
export function isPlayable(video: VideoItem): video is PlayableClip {
  return (
    (video.kind === 'youtube' && video.youtube !== null) ||
    (video.kind === 'video' && video.stream !== null)
  );
}

/** Which of the two players a playable clip goes to. */
export function isYoutubeClip(video: PlayableClip): video is YoutubeClip {
  return video.kind === 'youtube';
}

/** The clip in the player area: the one chosen, or — before any — the newest the player can play. */
export function shownClip(state: Pick<VideoState, 'items' | 'selected'>): VideoItem | undefined {
  return state.selected ?? state.items.find(isPlayable);
}

/** The playable clips on either side of the one shown. */
export interface Neighbours {
  /** Up the list, newer; `undefined` on the first. */
  readonly previous: PlayableClip | undefined;
  /** Down the list, older; `undefined` on the last loaded — the list may go on past it. */
  readonly next: PlayableClip | undefined;
}

/**
 * Task 75: the clips before and after `current` among the playable ones of the list. A clip the
 * pages loaded so far do not reach — opened from search, or kept from further down when the tab
 * reloaded its first page — has neither yet: its place is found by loading on (`engine/video.ts`).
 */
function neighbours(items: readonly VideoItem[], current: VideoItem | undefined): Neighbours {
  const playable = items.filter(isPlayable);
  const at = current === undefined ? -1 : playable.findIndex((item) => item.id === current.id);
  if (at === -1) return { previous: undefined, next: undefined };
  return { previous: playable[at - 1], next: playable[at + 1] };
}

/**
 * Task 77: the rows of «Избранные» whose heart is off now — taken off while the list is looked at,
 * or put on another phone and not yet in this one's set. They stay in sight and do not play.
 */
function heartlessRows(
  favoriteClips: readonly VideoItem[] | undefined,
  hearts: ReadonlySet<number>,
): number[] {
  return (favoriteClips ?? []).filter((clip) => !hearts.has(clip.id)).map((clip) => clip.id);
}

/** What plays in a row is made of: the slice's lists, and which rows of «Избранные» rest. */
export interface PlaylistInputs extends Pick<
  VideoState,
  'items' | 'nextCursor' | 'search' | 'view' | 'favoriteClips'
> {
  /** Task 77: the rows of «Избранные» whose heart is off now. */
  readonly heartless: readonly number[];
}

/** Task 76: what the list shows, and what plays in a row. */
export interface Playlist {
  readonly items: readonly VideoItem[];
  /** Narrowed by the listener — the field's answer, or «Избранные»: all of it, in one answer. */
  readonly narrowed: boolean;
  /** Nothing left to load: the end of the whole list, or a narrowed one. */
  readonly complete: boolean;
  /** Task 77: rows in sight that the row of clips passes over — their heart was taken off. */
  readonly skipped: ReadonlySet<number>;
}

const NOTHING_SKIPPED: ReadonlySet<number> = new Set();

/**
 * «Избранные» (task 77), narrowed by the field when it has an answer; else the clips the field
 * found (task 76); else the whole list.
 */
export function playlist(inputs: PlaylistInputs): Playlist {
  if (inputs.view === 'favorites') {
    const rows = inputs.favoriteClips ?? [];
    const found =
      inputs.search === undefined ? undefined : new Set(inputs.search.items.map((c) => c.id));
    const items = found === undefined ? rows : rows.filter((clip) => found.has(clip.id));
    return { items, narrowed: true, complete: true, skipped: new Set(inputs.heartless) };
  }
  if (inputs.search !== undefined) {
    return {
      items: inputs.search.items,
      narrowed: true,
      complete: true,
      skipped: NOTHING_SKIPPED,
    };
  }
  return {
    items: inputs.items,
    narrowed: false,
    complete: inputs.nextCursor === null,
    skipped: NOTHING_SKIPPED,
  };
}

/** Task 77: what plays this moment, both slices read now — for the engine, which acts on events. */
export function playlistNow(): Playlist {
  const state = useVideo.getState();
  return playlist({
    ...state,
    heartless: heartlessRows(state.favoriteClips, useFavorites.getState().ids),
  });
}

/**
 * Task 77: what plays, for a screen. Of the hearts it reads only those of the rows of «Избранные»,
 * so a heart elsewhere — a track's, on the mini player — redraws nothing here
 * (`store/favorites.ts`).
 */
export function usePlaylist(): Playlist {
  const items = useVideo((s) => s.items);
  const nextCursor = useVideo((s) => s.nextCursor);
  const search = useVideo((s) => s.search);
  const view = useVideo((s) => s.view);
  const favoriteClips = useVideo((s) => s.favoriteClips);
  const heartless = useFavorites(useShallow((s) => heartlessRows(favoriteClips, s.ids)));
  return playlist({ items, nextCursor, search, view, favoriteClips, heartless });
}

/**
 * The neighbours of `current` in what plays. A clip a narrowed list does not hold — one playing on
 * from before the search or the switch — has that list after it: «Дальше» starts its first. Task
 * 77: a row passed over is no one's neighbour, but the clip in the frame keeps its place even when
 * its own heart was taken off — the one after it comes next.
 */
export function aroundIn(list: Playlist, current: VideoItem | undefined): Neighbours {
  const plays =
    list.skipped.size === 0
      ? list.items
      : list.items.filter((item) => !list.skipped.has(item.id) || item.id === current?.id);
  if (list.narrowed && current !== undefined && !plays.some((item) => item.id === current.id)) {
    return { previous: undefined, next: plays.find(isPlayable) };
  }
  return neighbours(plays, current);
}

/** The neighbours of the clip in the player area — what «Назад» and «Дальше» would start now. */
export function aroundShown(): Neighbours {
  return aroundIn(playlistNow(), shownClip(useVideo.getState()));
}

/** Back to nothing. For tests: the store is module-level and outlives every screen. */
export function resetVideo(): void {
  latest += 1;
  pageInFlight = undefined;
  favoritesLatest += 1;
  notedSinceAsked = [];
  typing.abandon();
  useVideo.setState(EMPTY);
}
